/**
 * The /pricing catalogue model (pure, no React).
 *
 * Contract: docs/marketplace/pricing-catalogue-v2.md §2.1 / §7.
 *   - Only `version === 2 && isActive` plans are sellable and shown.
 *   - `track` (bachelors | masters | phd | mba) picks the column set,
 *     `tier` (1 premium, 2 ultra, 3 ultimate) orders the cards.
 *   - `planId` is identity only; never compare two planIds.
 *
 * Track selection order (first hit wins):
 *   `?level=` (any case) → the profile's aspiring degree level →
 *   localStorage `pricing.level` → "masters".
 */

export const CATALOGUE_VERSION = 2;

/** Tracks in display order (the segmented control). */
export const TRACKS = [
  { key: "bachelors", label: "Bachelors" },
  { key: "masters", label: "Masters" },
  { key: "phd", label: "PhD" },
  { key: "mba", label: "MBA" },
];

export const DEFAULT_TRACK = "masters";
export const TRACK_STORAGE_KEY = "pricing.level";
export const LEVEL_QUERY_PARAM = "level";

const TRACK_KEYS = TRACKS.map((t) => t.key);

/** "Masters" / "MBA " / "phd" → track key; anything else → null. */
export function normaliseTrack(value) {
  const key = String(value || "").trim().toLowerCase();
  return TRACK_KEYS.includes(key) ? key : null;
}

export const trackLabel = (key) => TRACKS.find((t) => t.key === key)?.label || "";

/**
 * Track from the profile's aspiring degree level
 * (`associatedDegreeLevel.aspiring[0].degreeLevel`). MBA cannot be inferred
 * (an MBA aspirant is stored as a master's level), so it never returns "mba".
 */
export function trackFromDegreeLevel(degreeLevel) {
  if (!degreeLevel) return null;
  const raw = `${degreeLevel.parentKey || ""} ${degreeLevel.enShortName || ""}`.toLowerCase();
  if (/\b(phd|ph\.d|doctor\w*|doctoral)\b/.test(raw)) return "phd";
  if (/\b(master\w*|pg|postgrad\w*)\b/.test(raw)) return "masters";
  if (/\b(bachelor\w*|ug|undergrad\w*)\b/.test(raw)) return "bachelors";
  return null;
}

/** The first aspiring degree level of a gamePlayerProfile → track (or null). */
export function trackFromProfile(gamePlayerProfile) {
  const aspiring = gamePlayerProfile?.associatedDegreeLevel?.aspiring;
  const first = Array.isArray(aspiring) ? aspiring[0] : null;
  return trackFromDegreeLevel(first?.degreeLevel);
}

/** Resolves the track to show; every input is optional. */
export function resolveTrack({ queryLevel, profile, storedLevel } = {}) {
  return (
    normaliseTrack(queryLevel) ||
    trackFromProfile(profile) ||
    normaliseTrack(storedLevel) ||
    DEFAULT_TRACK
  );
}

/** Sellable plans of the current catalogue. */
export const isCataloguePlan = (plan) =>
  Boolean(plan) && plan.version === CATALOGUE_VERSION && plan.isActive !== false && Boolean(normaliseTrack(plan.track));

/** `{ [track]: plans sorted by tier }` for the sellable v2 plans. */
export function groupCatalogue(plans = []) {
  const byTrack = {};
  (plans || []).filter(isCataloguePlan).forEach((plan) => {
    const key = normaliseTrack(plan.track);
    (byTrack[key] = byTrack[key] || []).push(plan);
  });
  Object.values(byTrack).forEach((list) => list.sort((a, b) => (a.tier || 0) - (b.tier || 0)));
  return byTrack;
}

/** The card subtitle: `tagline`, else the seed's `description`. */
export const planTagline = (plan) => plan?.tagline || plan?.description || "";

/** "Masters Premium" — tier labels repeat across tracks, so carts/tracking need the track. */
export const planFullName = (plan) =>
  [trackLabel(normaliseTrack(plan?.track)), plan?.displayName || plan?.planName].filter(Boolean).join(" ");

const UNIVERSITY_ROW = /^(universities|schools)$/i;

/**
 * "Apply to up to 10 universities" from the features row named
 * "Universities" (MBA: "Schools"); null when the row or a number is missing.
 */
export function applyLimitLine(plan) {
  const row = (plan?.features || []).find((f) => UNIVERSITY_ROW.test(String(f?.name || "").trim()));
  const count = row ? parseInt(String(row.value ?? ""), 10) : NaN;
  if (!Number.isFinite(count)) return null;
  return `Apply to up to ${count} ${row.name.trim().toLowerCase()}`;
}
